import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import { withStyles, makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Grid,
  Tooltip,
  LinearProgress,
  Typography,
  InputBase,
} from "@material-ui/core";

import FileCopyIcon from "@mui/icons-material/FileCopy";
import GetAppIcon from "@mui/icons-material/GetApp";
import SearchIcon from "@mui/icons-material/Search";

import { utils, writeFileXLSX } from "xlsx";

import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import DialogExportFinancialTitles from "components/Dialogs/DialogExportFinancialTitles";
import UtilsFncs from "utils/UtilsFncs";
import {
  columnDateField,
  columnPriceField,
  sortableField,
  columnNumberField,
} from "utils/tables-utils";
import useLogs from "hooks/logs";
import PrintButton from "components/_refactor/button/print";
import componentStyles from "assets/theme/components/dropdowns/notifications-dropdown.js";
import componentNavbarStyles from "assets/theme/components/navbars/admin-navbar.js";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import { IsMobile } from "utils/is-mobile";
const useNavbarStyles = makeStyles(componentNavbarStyles);
const useStyles = makeStyles(componentStyles);

const exportIconStyle = {
  width: 25,
  height: 25,
  marginLeft: 15,
};

const ColorButton = withStyles((theme) => ({
  root: {
    "&:hover": {
      color: theme.palette.white.main,
    },
  },
}))(Button);

const columnModel = (downloadBoleto, copiarLinhaDigitavel) => [
  {
    dataField: "boleto",
    text: "Boleto",
    headerAlign: "center",
    align: "center",
    isDummyField: true,
    formatter: downloadBoleto,
    csvExport: false,
    footer: () =>
      "Boletos geralmente são emitidos 30 dias antes do vencimento do título.",
  },
  // {
  //   dataField: "Id",
  //   text: "ID",
  //   sort: true,
  // sortCaret: () => {
  //   return null;
  // }
  // },
  columnNumberField(
    {
      dataField: "NumeroTitulo",
      text: "Nr Título",
    },
    true
  ),
  columnNumberField(
    {
      dataField: "Situacao",
      text: "Situação",
      formatter: (cell, row) => {
        return (
          <Tooltip
            title={
              (row.Situacao === "AB" && "Aberto") ||
              (row.Situacao === "CA" && "Cancelado") ||
              (row.Situacao === "LQ" && "Liquidado") ||
              (row.Situacao === "LS" && "Liquidado Substituído")
            }
          >
            <span>{row.Situacao}</span>
          </Tooltip>
        );
      },
    },
    true
  ),
  columnDateField(
    {
      dataField: "DataEmissao",
      text: "Emissão",
    },
    true
  ),
  columnDateField(
    {
      dataField: "DtVencimento",
      text: "Vencimento",
    },
    true
  ),
  columnDateField(
    {
      dataField: "DtVencimentoProrrogado",
      text: "Vencimento Prorrogado",
    },
    true
  ),
  columnPriceField(
    {
      dataField: "Valor",
      text: "Valor",
    },
    true
  ),
  columnPriceField(
    {
      dataField: "ValorTotalNF",
      text: "Valor Total NF",
    },
    true
  ),
  columnNumberField(
    {
      dataField: "NumeroNf",
      text: "Nr NF",
    },
    true
  ),
  sortableField({
    dataField: "Observacao",
    text: "Observação",
  }),
  columnNumberField(
    {
      dataField: "NumeroTituloBanco",
      text: "Nr Título Banco",
    },
    true
  ),
  sortableField({
    dataField: "LinhaDigitavelBanco",
    text: "Linha Digitável Banco",
    csvText: "Linha Digitavel Banco",
    headerAlign: "center",
    align: "center",
    hidden: true,
  }),
  sortableField({
    dataField: "copiar",
    text: "Linha Digitável Banco",
    formatter: copiarLinhaDigitavel,
    csvText: "Linha Digitavel Banco",
    headerAlign: "center",
    align: "center",
    csvExport: false,
  }),
  columnNumberField(
    {
      dataField: "NumeroPedido",
      text: "Nr. Pedido",
    },
    true
  ),
];

const filterNonSelectableTitles = (dataTable) => {
  const nonSelectableTitlesIndexes = [];

  dataTable?.forEach((item, index) => {
    if (!(item.UrlDownloadBoleto !== null && item.Situacao !== "LQ")) {
      nonSelectableTitlesIndexes.push({ index: index, item: item.Id });
    }
  });

  return nonSelectableTitlesIndexes;
};

const filterSelectableTitles = (dataTable) => {
  const selectableIndexes = [];

  dataTable?.forEach((item, index) => {
    if (item.UrlDownloadBoleto && item.Situacao !== "LQ") {
      selectableIndexes.push(item.Id);
    }
  });

  return selectableIndexes;
};

const filterSelectedTitles = (dataTable, selectedTitles) => {
  const listItems = [];
  dataTable?.map((filter) => {
    if (selectedTitles.includes(filter.Id)) {
      listItems.push(filter);
    }
  });
  return listItems;
};

const getFinancialTitlesList = (dataTable) => {
  const financialList = [];

  dataTable?.forEach((data) => {
    const newData = {
      NumeroTitulo: data?.NumeroTitulo,
      Situacao: data?.Situacao,
      Emissao: data?.DataEmissaoFormat,
      Vencimento: data?.DtVencimentoFormat,
      VencimentoProrrogado: data?.DtVencimentoProrrogadoFormat,
      Valor: data?.Valor,
      ValorTotal: data?.ValorTotalNF,
      NrNF: data?.NumeroNf,
      Observacao: data?.Observacao,
      NrTituloBanco: data?.NumeroTituloBanco,
      LinhaDigitavel: data?.LinhaDigitavelBanco,
      NrPedido: data?.NumeroPedido,
    };

    financialList.push(newData);
  });

  return financialList;
};

const TabelaTitulos = ({ dataTable, labelDias, storePhone }) => {
  const { setLocalStorageActions } = useLogs();
  const classes = {
    ...useStyles(),
    ...useNavbarStyles(),
  };

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [fullDataTable, setFullDataTable] = useState([]);
  const [searchItemsList, setSearchItemsList] = useState(null);
  const [searchingItems, setSearchingItems] = useState(false);

  const isMobile = IsMobile();

  const [nonSelectableTitles, setNonSelectableTitles] = useState(
    filterNonSelectableTitles(dataTable)
  );
  const [selectableTitles, setSelectableTitles] = useState(
    filterSelectableTitles(dataTable)
  );
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [openExportBoletos, setOpenExportBoletos] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setNonSelectableTitles(filterNonSelectableTitles(dataTable));
    setSelectableTitles(filterSelectableTitles(dataTable));
    setFullDataTable(dataTable);
  }, [dataTable]);

  const downloadBoleto = (cell, row) => {
    return (
      <div>
        <ColorButton
          color="default"
          variant="contained"
          size="small"
          href={row.UrlDownloadBoleto}
          target="_blank"
          disabled={
            !(row.UrlDownloadBoleto && row.Situacao !== "LQ") ||
            !row.UrlDownloadBoleto
          }
          onClick={() => {
            setLocalStorageActions({
              nomeTela: "Lojista 360",
              idMenu: "10",
              acao: "Clicou em Download Boleto aba Títulos Financeiros",
            });
          }}
        >
          Boleto
        </ColorButton>
      </div>
    );
  };

  const copiarLinhaDigitavel = (cell, row) => {
    return (
      <div>
        {row.Situacao !== "LQ" &&
        Boolean(row.LinhaDigitavelBanco) &&
        row.LinhaDigitavelBanco.trim() > "" ? (
          <Tooltip title={row.LinhaDigitavelBanco} placement="bottom">
            <span>
              <IconButton
                title="Copiar Linha Digitável Banco"
                size="small"
                onClick={() => {
                  UtilsFncs.CopyToClipboard(row.LinhaDigitavelBanco);
                  setLocalStorageActions({
                    nomeTela: "Lojista 360",
                    idMenu: "10",
                    acao: "Clicou em Copiar Linha Digitável aba Títulos Financeiros",
                  });
                }}
              >
                <FileCopyIcon fontSize="large"></FileCopyIcon>
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          ""
        )}
      </div>
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked && !selectedTitles.length) {
      setIsAllChecked(e.target.checked);
      setIsChecked(true);
      setSelectedTitles(selectableTitles);
    } else {
      setSelectedTitles([]);
      setIsAllChecked(false);
      setIsChecked(false);
    }
  };

  const handleShowExportButton = () => {
    if ((selectedTitles && selectedTitles.length > 0) || isChecked) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="row"
          width="100%"
        >
          <Button
            variant="contained"
            size="small"
            style={{
              display: "flex",
              marginLeft: "1rem",
              marginRight: "0.5rem",
            }}
            startIcon={<GetAppIcon style={{ width: 18, height: 18 }} />}
            onClick={toggleExportBoletosDialog}
          >
            Exportar Boletos
          </Button>
        </Box>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    handleShowExportButton();
  }, [selectedTitles, isAllChecked]);

  const handleSelect = useCallback(
    (e, rowIndex) => {
      if (e.target.checked) {
        setIsChecked(true);
        const updatedSelectedTitles = (selectedTitles || []).concat(rowIndex);
        setSelectedTitles(updatedSelectedTitles);
      } else {
        setIsChecked(false);
        const updatedSelectedTitles = (selectedTitles || []).filter(
          (index) => index !== rowIndex
        );
        setSelectedTitles(updatedSelectedTitles);
      }
    },
    [selectedTitles]
  );

  const toggleExportBoletosDialog = useCallback(async () => {
    setLocalStorageActions({
      nomeTela: "Lojista 360",
      idMenu: "10",
      acao: "Clicou em Exportar boleto aba Títulos Financeiros",
    });
    setOpenExportBoletos(!openExportBoletos);
  }, [openExportBoletos]);

  const selectRow = {
    mode: "checkbox",
    nonSelectableTitles,
    onSelect: (row, isSelect, rowIndex) => {
      if (!(row.UrlDownloadBoleto !== null && row.Situacao !== "LQ")) {
        return false;
      } else if (
        isSelect &&
        !selectedTitles.includes(row.Id) &&
        row.UrlDownloadBoleto
      ) {
        setIsChecked(true);
        const updatedSelectedTitles = (selectedTitles || []).concat(row.Id);
        setSelectedTitles(updatedSelectedTitles);
      } else {
        setIsChecked(false);
        const updatedSelectedTitles = (selectedTitles || []).filter(
          (index) => index !== row.Id
        );

        setSelectedTitles(updatedSelectedTitles);
      }
    },
    selectionRenderer: ({ rowIndex, rowKey, ...props }) => {
      const isNonSelectableTitle = nonSelectableTitles?.map((nonSelectable) => {
        if (nonSelectable.item === rowKey && nonSelectable.index === rowIndex) {
          return true;
        } else {
          return false;
        }
      });

      if (searchItemsList?.length !== 0 && searchItemsList !== null) {
        return (
          <>
            {searchItemsList?.map((data) => {
              if (data.Id === rowKey) {
                return (
                  <Checkbox
                    key={data.Id}
                    {...props}
                    disabled={!isNonSelectableTitle}
                    checked={
                      (data.Situacao === "LQ" || !data.UrlDownloadBoleto) &&
                      isNonSelectableTitle
                        ? false
                        : isChecked
                    }
                    onClick={(e) => handleSelect(e, rowKey)}
                    color="primary"
                  />
                );
              }
            })}
          </>
        );
      } else {
        return (
          <>
            {fullDataTable?.map((data) => {
              if (data.Id === rowKey) {
                return (
                  <Checkbox
                    key={data.Id}
                    {...props}
                    disabled={!isNonSelectableTitle}
                    checked={
                      (data.Situacao === "LQ" || !data.UrlDownloadBoleto) &&
                      isNonSelectableTitle
                        ? false
                        : isChecked
                    }
                    onClick={(e) => handleSelect(e, rowKey)}
                    color="primary"
                  />
                );
              }
            })}
          </>
        );
      }
    },
    selectionHeaderRenderer: ({ rowIndex, rowKey, ...props }) => {
      return (
        <Checkbox
          {...props}
          color="primary"
          onClick={(e) => handleSelectAll(e)}
          checked={isAllChecked}
        />
      );
    },
  };

  const handleClick = () => {
    if (searchValue !== "") {
      setSearchingItems(true);
      const listItens = [];
      fullDataTable?.map((item) => {
        if (
          item?.NumeroTitulo?.includes(searchValue) ||
          item?.NumeroPedido?.includes(searchValue) ||
          item?.LinhaDigitavelBanco?.includes(searchValue) ||
          item?.NumeroTituloBanco?.includes(searchValue) ||
          item?.Observacao?.includes(searchValue) ||
          item?.NumeroNf?.toString()?.includes(searchValue) ||
          item?.ValorTotalNF?.toString()?.includes(searchValue) ||
          item?.Valor?.toString()?.includes(searchValue) ||
          item?.DtVencimentoProrrogadoFormat?.includes(searchValue) ||
          item?.DtVencimentoFormat?.includes(searchValue) ||
          item?.DataEmissaoFormat?.includes(searchValue)
        ) {
          listItens.push(item);
        }
      });
      setSearchItemsList(listItens);
      setNonSelectableTitles(filterNonSelectableTitles(listItens));
      setSelectableTitles(filterSelectableTitles(listItens));
      setTimeout(() => {
        setSearchingItems(false);
      }, 500);
    }
  };

  const handleValueChange = useCallback((e) => {
    if (e.target.value === "") {
      setSearchItemsList(null);
    }
    setSearchValue(e.target.value);
    searchInputRef.current.focus();
  }, []);

  const dataFinancialTitles = useMemo(
    () =>
      searchItemsList?.length !== 0 && searchItemsList !== null
        ? getFinancialTitlesList(searchItemsList)
        : fullDataTable?.length !== 0
        ? getFinancialTitlesList(fullDataTable)
        : [],
    [fullDataTable, searchItemsList]
  );

  function handleDownloadXML() {
    const receipt = utils.json_to_sheet(dataFinancialTitles);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, receipt, "TitulosFinanceiros");
    writeFileXLSX(wb, "TitulosFinanceiros.xlsx");
  }

  const exportCmp =
    searchItemsList?.length !== 0 && searchItemsList !== null ? (
      <>
        <span>
          <Tooltip title="Exportar tudo">
            <IconButtonLoader
              onClick={() => handleDownloadXML()}
              icon={
                <img
                  src="https://img.icons8.com/material-outlined/72/000000/xls-export.png"
                  alt="ícone clicável para exportar a tabela no formato Excel"
                  style={exportIconStyle}
                />
              }
              size="small"
            />
          </Tooltip>
        </span>
      </>
    ) : (
      <>
        {fullDataTable?.length !== 0 ? (
          <>
            <span>
              <Tooltip title="Exportar tudo">
                <IconButtonLoader
                  onClick={() => handleDownloadXML()}
                  icon={
                    <img
                      src="https://img.icons8.com/material-outlined/72/000000/xls-export.png"
                      alt="ícone clicável para exportar a tabela no formato Excel"
                      style={exportIconStyle}
                    />
                  }
                  size="small"
                />
              </Tooltip>
            </span>
          </>
        ) : null}
      </>
    );

  useEffect(() => {
    if (searchItemsList?.length === 0) {
      setTimeout(() => {
        setSearchItemsList(null);
      }, 2500);
    }
  }, [searchItemsList]);

  return (
    <>
      {alert}
      <>
        <Grid item xs={12}>
          {searchingItems ? (
            <div style={{ marginTop: "1rem" }}>
              <span>Buscando dados..</span>
              <LinearProgress />
            </div>
          ) : (
            <ToolkitTable
              searched={Boolean(fullDataTable)}
              keyField="Id"
              dataTable={searchItemsList ? searchItemsList : fullDataTable}
              columnModel={columnModel(downloadBoleto, copiarLinhaDigitavel)}
              pagination={{
                options: [25, 50, 100],
                defaultOption: 25,
                totalSize: searchItemsList
                  ? searchItemsList?.length
                  : fullDataTable?.length,
                showTotal: true,
              }}
              notFoundTitle="Sua pesquisa não encontrou nenhum título correspondente."
              bootstrapTableProps={{
                selectRow,
              }}
              hasFooter
              CustomHeader={({ componentRefProps, selectRefProps }) => (
                <Grid
                  container
                  className="pl-4"
                  style={{ padding: "0 24px 12px" }}
                >
                  <Grid
                    item
                    xs={isMobile ? 12 : 6}
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: isMobile ? "1rem" : 0,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Box
                      component={Typography}
                      variant="h3"
                      marginBottom="0!important"
                    >
                      <Box component="span">
                        {typeof labelDias === "string"
                          ? `Financeiro - Títulos entre ${labelDias}`
                          : `Financeiro - Títulos nos últimos ${labelDias} dias`}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      ref={searchRef}
                      alignItems="center"
                      width="auto"
                      component={Grid}
                      className={classes.searchBox}
                      style={{ marginLeft: isMobile ? 0 : "1rem" }}
                    >
                      <Tooltip title={"Pesquisar"}>
                        <SearchIcon
                          className={classes.searchIcon}
                          onClick={handleClick}
                          style={{ cursor: "pointer" }}
                          fontSize="large"
                        />
                      </Tooltip>
                      <InputBase
                        inputRef={searchInputRef}
                        placeholder="Faça a sua busca"
                        classes={{
                          input: classes.searchInput,
                        }}
                        value={searchValue}
                        onChange={handleValueChange}
                        autoComplete="off"
                        name="search"
                        autoFocus
                        inputProps={{
                          autoFocus: true,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : 6}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isMobile ? "flex-start" : "flex-end",
                      padding: 0,
                      marginBottom: "0.5rem!important",
                    }}
                  >
                    <Box
                      justifyContent="flex-end"
                      alignItems="center"
                      display="flex"
                      flexWrap="wrap"
                      style={{ marginLeft: isMobile && 20, cursor: "pointer" }}
                    >
                      {handleShowExportButton()}
                    </Box>
                    <Box
                      justifyContent="flex-end"
                      alignItems="center"
                      display="flex"
                      flexWrap="wrap"
                      style={{
                        marginLeft: !isMobile && 15,
                        marginRight: 15,
                        cursor: "pointer",
                      }}
                    >
                      {exportCmp}
                    </Box>
                    <Box
                      justifyContent="flex-end"
                      alignItems="center"
                      display="flex"
                      flexWrap="wrap"
                      style={{
                        marginLeft: 15,
                        marginRight: 15,
                        cursor: "pointer",
                      }}
                    >
                      <PrintButton
                        selectRef={selectRefProps}
                        componentRef={componentRefProps}
                        dataTable={
                          searchItemsList ? searchItemsList : fullDataTable
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            />
          )}
        </Grid>
      </>
      {openExportBoletos && (
        <DialogExportFinancialTitles
          open={openExportBoletos}
          handleClose={toggleExportBoletosDialog}
          titles={
            searchItemsList?.length !== 0 && searchItemsList !== null
              ? filterSelectedTitles(searchItemsList, selectedTitles)
              : filterSelectedTitles(fullDataTable, selectedTitles)
          }
          storePhone={storePhone ? storePhone : ""}
        />
      )}
    </>
  );
};

TabelaTitulos.propTypes = {
  dataTable: PropTypes.array,
  labelDias: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TabelaTitulos;
